import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Zap, Play, Clock, CheckCircle, AlertCircle, Database, Filter } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { TestResult, TestLog } from "./TestingComponent";
import { loadRealUserData, generateRealTestPayloads, type RealDataContext } from "@/utils/realDataLoader";
import { 
  generateTwilioSid,
  generateIndustryScenario,
  type IndustryScenario 
} from "@/utils/realisticDataGenerators";

interface EdgeFunctionMonitorProps {
  onTestResult: (result: TestResult) => void;
  onTestLog: (log: Omit<TestLog, 'id' | 'timestamp'>) => void;
}

interface EdgeFunctionTest {
  name: string;
  functionName: string;
  description: string;
  testPayload: any;
  expectedFields?: string[];
  requiresAuth?: boolean;
  category: 'core' | 'voice' | 'sms' | 'ai' | 'admin' | 'security' | 'integration';
}

type FunctionCategory = 'all' | 'core' | 'voice' | 'sms' | 'ai' | 'admin' | 'security' | 'integration';

export const EdgeFunctionMonitor = forwardRef<{ testAllFunctions: () => Promise<void> }, EdgeFunctionMonitorProps>(({
  onTestResult,
  onTestLog,
}, ref) => {
  const [selectedFunction, setSelectedFunction] = useState<string>('Calie_status');
  const [selectedCategory, setSelectedCategory] = useState<FunctionCategory>('all');
  const [customPayload, setCustomPayload] = useState<string>('{}');
  const [testing, setTesting] = useState<string>('');
  const [realData, setRealData] = useState<RealDataContext | null>(null);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [industryScenario, setIndustryScenario] = useState<IndustryScenario | null>(null);
  const [functionStatuses, setFunctionStatuses] = useState<Record<string, 'passed' | 'failed' | 'pending'>>({});

  // Load real user data on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      const data = await loadRealUserData();
      setRealData(data);
      setLoadingData(false);
      
      if (data) {
        const realPayloads = generateRealTestPayloads(data);
        setCustomPayload(JSON.stringify(realPayloads.twilioValidation, null, 2));
        
        const industries = ['real_estate', 'automotive', 'healthcare', 'ecommerce', 'professional'];
        const randomIndustry = industries[Math.floor(Math.random() * industries.length)];
        const scenario = generateIndustryScenario(randomIndustry);
        setIndustryScenario(scenario);
      }
    };
    loadData();
  }, []);

  const getEdgeFunctions = useCallback((): EdgeFunctionTest[] => {
    const baseTenantId = realData?.tenant?.tenant_id || 'test-tenant-id';
    const basePayload = realData ? generateRealTestPayloads(realData) : null;
    
    const allFunctions: EdgeFunctionTest[] = [
      // === CORE ===
      {
        name: 'Status Check',
        functionName: 'Calie_status',
        description: 'Basic health check and system status',
        testPayload: {},
        expectedFields: ['status', 'timestamp'],
        requiresAuth: false,
        category: 'core',
      },
      {
        name: 'Twilio Validation',
        functionName: 'Calie_twilioValidate',
        description: `Validate Twilio credentials`,
        testPayload: basePayload?.twilioValidation || { tenantId: baseTenantId },
        expectedFields: ['valid'],
        requiresAuth: true,
        category: 'core',
      },
      {
        name: 'Reset Onboarding',
        functionName: 'Calie_reset_onboarding',
        description: 'Reset tenant onboarding state',
        testPayload: basePayload?.resetOnboarding || { tenantId: baseTenantId },
        expectedFields: ['success'],
        requiresAuth: true,
        category: 'core',
      },
      {
        name: 'Fix Credentials',
        functionName: 'Calie_fix_credentials',
        description: 'Repair Twilio credentials',
        testPayload: basePayload?.fixCredentials || { tenantId: baseTenantId },
        expectedFields: ['fixed'],
        requiresAuth: true,
        category: 'core',
      },
      
      // === VOICE/CALL ===
      {
        name: 'Voice Webhook',
        functionName: 'Calie_voice',
        description: 'Handle incoming voice calls from Twilio',
        testPayload: {
          CallSid: generateTwilioSid('CA'),
          From: '+15551234567',
          To: realData?.numbers?.[0]?.e164 || '+15559876543',
          CallStatus: 'ringing',
        },
        expectedFields: ['Response'],
        requiresAuth: false,
        category: 'voice',
      },
      {
        name: 'Handle Recording',
        functionName: 'Calie_handleRecording',
        description: 'Process call recording callbacks',
        testPayload: {
          CallSid: generateTwilioSid('CA'),
          RecordingSid: generateTwilioSid('RE'),
          RecordingUrl: 'https://api.twilio.com/recordings/test',
          RecordingDuration: '120',
        },
        expectedFields: ['success'],
        requiresAuth: false,
        category: 'voice',
      },
      {
        name: 'Process Call Complete',
        functionName: 'Calie_processCallComplete',
        description: 'Handle call completion events',
        testPayload: {
          CallSid: generateTwilioSid('CA'),
          CallStatus: 'completed',
          CallDuration: '180',
        },
        expectedFields: ['processed'],
        requiresAuth: false,
        category: 'voice',
      },
      {
        name: 'Get Recording',
        functionName: 'Calie_getRecording',
        description: 'Retrieve recording audio',
        testPayload: {
          recordingId: 'test-recording-id',
          tenantId: baseTenantId,
        },
        expectedFields: ['url'],
        requiresAuth: true,
        category: 'voice',
      },
      {
        name: 'Configure Voice Webhook',
        functionName: 'Calie_configureVoiceWebhook',
        description: 'Configure voice webhooks for numbers',
        testPayload: basePayload?.configureVoiceWebhook || { tenantId: baseTenantId },
        expectedFields: ['configured'],
        requiresAuth: true,
        category: 'voice',
      },
      
      // === SMS ===
      {
        name: 'Send SMS',
        functionName: 'Calie_sendSMS',
        description: 'Send outbound SMS message',
        testPayload: {
          toNumber: '+15551234567',
          messageBody: 'Test message from Calie.ai',
          tenantId: baseTenantId,
        },
        expectedFields: ['messageSid', 'status'],
        requiresAuth: true,
        category: 'sms',
      },
      {
        name: 'Missed Call SMS',
        functionName: 'Calie_missedCallSMS',
        description: 'Send SMS for missed calls',
        testPayload: {
          CallSid: generateTwilioSid('CA'),
          From: '+15551234567',
          To: realData?.numbers?.[0]?.e164 || '+15559876543',
        },
        expectedFields: ['sent'],
        requiresAuth: false,
        category: 'sms',
      },
      {
        name: 'Inbound SMS',
        functionName: 'Calie_inboundSMS',
        description: 'Handle incoming SMS messages',
        testPayload: {
          MessageSid: generateTwilioSid('SM'),
          From: '+15551234567',
          To: realData?.numbers?.[0]?.e164 || '+15559876543',
          Body: 'Test inbound message',
        },
        expectedFields: ['Response'],
        requiresAuth: false,
        category: 'sms',
      },
      {
        name: 'Debug Inbound SMS',
        functionName: 'Calie_debugInboundSMS',
        description: 'Debug SMS webhook configuration',
        testPayload: {
          tenantId: baseTenantId,
        },
        expectedFields: ['debug'],
        requiresAuth: true,
        category: 'sms',
      },
      {
        name: 'Get MMS Media',
        functionName: 'Calie_getMMSMedia',
        description: 'Retrieve MMS media attachments',
        testPayload: {
          mediaUrl: 'https://api.twilio.com/test-media',
          accountSid: generateTwilioSid('AC'),
        },
        expectedFields: [],
        requiresAuth: true,
        category: 'sms',
      },
      
      // === AI/ML ===
      {
        name: 'Transcribe',
        functionName: 'Calie_transcribe',
        description: 'Transcribe call recordings',
        testPayload: {
          callId: 'test-call-id',
          recordingUrl: 'https://api.twilio.com/recordings/test',
        },
        expectedFields: ['transcript'],
        requiresAuth: true,
        category: 'ai',
      },
      {
        name: 'Transcribe Batch',
        functionName: 'Calie_transcribeBatch',
        description: 'Batch transcribe multiple recordings',
        testPayload: {
          tenantId: baseTenantId,
          limit: 5,
        },
        expectedFields: ['processed'],
        requiresAuth: true,
        category: 'ai',
      },
      {
        name: 'Summarize',
        functionName: 'Calie_summarize',
        description: 'Generate AI summary of call',
        testPayload: {
          callId: 'test-call-id',
          transcript: 'Hello, I am interested in your services. Can you tell me more about pricing?',
        },
        expectedFields: ['summary'],
        requiresAuth: true,
        category: 'ai',
      },
      {
        name: 'Generate Embedding',
        functionName: 'Calie_generateEmbedding',
        description: 'Generate vector embeddings for search',
        testPayload: {
          text: 'Sample text for embedding generation',
          tenantId: baseTenantId,
        },
        expectedFields: ['embedding'],
        requiresAuth: true,
        category: 'ai',
      },
      {
        name: 'Search Transcripts',
        functionName: 'Calie_searchTranscripts',
        description: 'Semantic search through transcripts',
        testPayload: {
          query: 'pricing information',
          tenantId: baseTenantId,
          limit: 10,
        },
        expectedFields: ['results'],
        requiresAuth: true,
        category: 'ai',
      },
      {
        name: 'Validate WER',
        functionName: 'Calie_validateWER',
        description: 'Calculate Word Error Rate',
        testPayload: {
          hypothesis: 'hello world',
          reference: 'hello word',
        },
        expectedFields: ['wer'],
        requiresAuth: true,
        category: 'ai',
      },
      
      // === ADMIN ===
      {
        name: 'Admin List Numbers',
        functionName: 'Calie_adminListNumbers',
        description: 'List all numbers (admin only)',
        testPayload: { limit: 10 },
        expectedFields: ['numbers'],
        requiresAuth: true,
        category: 'admin',
      },
      {
        name: 'Admin List Tenants',
        functionName: 'Calie_adminListTenants',
        description: 'List all tenants (admin only)',
        testPayload: { limit: 10 },
        expectedFields: ['tenants'],
        requiresAuth: true,
        category: 'admin',
      },
      {
        name: 'Buy Number',
        functionName: 'Calie_buyNumber',
        description: 'Purchase phone number from Twilio',
        testPayload: basePayload?.buyNumber || { tenantId: baseTenantId, areaCode: '415' },
        expectedFields: ['phoneNumber'],
        requiresAuth: true,
        category: 'admin',
      },
      {
        name: 'Buy Ad Number',
        functionName: 'Calie_buyNumberForAd',
        description: 'Purchase number for ad campaign',
        testPayload: basePayload?.buyNumberForAd || { tenantId: baseTenantId, adId: 'test-ad-id' },
        expectedFields: ['phoneNumber'],
        requiresAuth: true,
        category: 'admin',
      },
      {
        name: 'Sync Numbers',
        functionName: 'Calie_syncNumbers',
        description: 'Sync numbers from Twilio',
        testPayload: basePayload?.syncNumbers || { tenantId: baseTenantId },
        expectedFields: ['numbers'],
        requiresAuth: true,
        category: 'admin',
      },
      {
        name: 'DNI Script',
        functionName: 'Calie_dniScript',
        description: 'Generate DNI tracking script',
        testPayload: basePayload?.dniScript || { tenantId: baseTenantId },
        expectedFields: ['script'],
        requiresAuth: false,
        category: 'admin',
      },
      
      // === SECURITY ===
      {
        name: 'Encrypt Token',
        functionName: 'Calie_encrypt_token',
        description: 'Encrypt sensitive tokens',
        testPayload: {
          token: 'test-token-to-encrypt',
          tenantId: baseTenantId,
        },
        expectedFields: ['encrypted'],
        requiresAuth: true,
        category: 'security',
      },
      {
        name: 'Enable 2FA',
        functionName: 'Calie_enable2FA',
        description: 'Generate 2FA setup for user',
        testPayload: {
          userId: realData?.user?.id || 'test-user-id',
        },
        expectedFields: ['secret', 'qrCode'],
        requiresAuth: true,
        category: 'security',
      },
      {
        name: 'Verify 2FA',
        functionName: 'Calie_verify2FA',
        description: 'Verify 2FA code',
        testPayload: {
          userId: realData?.user?.id || 'test-user-id',
          code: '123456',
        },
        expectedFields: ['valid'],
        requiresAuth: true,
        category: 'security',
      },
      {
        name: 'Monitor Errors',
        functionName: 'Calie_monitorErrors',
        description: 'Monitor system errors and alerts',
        testPayload: {
          tenantId: baseTenantId,
        },
        expectedFields: ['errors'],
        requiresAuth: true,
        category: 'security',
      },
      {
        name: 'Send Alert',
        functionName: 'Calie_sendAlert',
        description: 'Send system alert notification',
        testPayload: {
          type: 'test',
          message: 'Test alert from testing suite',
          tenantId: baseTenantId,
        },
        expectedFields: ['sent'],
        requiresAuth: true,
        category: 'security',
      },
      
      // === INTEGRATION ===
      {
        name: 'Auto Create Subaccount',
        functionName: 'Calie_autoCreateSubaccount',
        description: 'Create Twilio subaccount',
        testPayload: basePayload?.autoCreateSubaccount || { tenantId: baseTenantId },
        expectedFields: ['subaccountSid'],
        requiresAuth: true,
        category: 'integration',
      },
      {
        name: 'Create Subaccount',
        functionName: 'Calie_create_subaccount',
        description: 'Manual subaccount creation',
        testPayload: {
          tenantId: baseTenantId,
          friendlyName: 'Test Subaccount',
        },
        expectedFields: ['subaccountSid'],
        requiresAuth: true,
        category: 'integration',
      },
      {
        name: 'List Subaccounts',
        functionName: 'Calie_listSubaccounts',
        description: 'List Twilio subaccounts',
        testPayload: { tenantId: baseTenantId },
        expectedFields: ['subaccounts'],
        requiresAuth: true,
        category: 'integration',
      },
      {
        name: 'Link Subaccount',
        functionName: 'Calie_linkSubaccount',
        description: 'Link subaccount to tenant',
        testPayload: {
          tenantId: baseTenantId,
          subaccountSid: generateTwilioSid('AC'),
        },
        expectedFields: ['linked'],
        requiresAuth: true,
        category: 'integration',
      },
      {
        name: 'Unlink Subaccount',
        functionName: 'Calie_unlinkSubaccount',
        description: 'Unlink subaccount from tenant',
        testPayload: {
          tenantId: baseTenantId,
        },
        expectedFields: ['unlinked'],
        requiresAuth: true,
        category: 'integration',
      },
      {
        name: 'Update Subaccount',
        functionName: 'Calie_updateSubaccount',
        description: 'Update subaccount settings',
        testPayload: {
          tenantId: baseTenantId,
          friendlyName: 'Updated Name',
        },
        expectedFields: ['updated'],
        requiresAuth: true,
        category: 'integration',
      },
      {
        name: 'Google Integration',
        functionName: 'Calie_google',
        description: 'Google OAuth callback handler',
        testPayload: { action: 'status', tenantId: baseTenantId },
        expectedFields: ['status'],
        requiresAuth: true,
        category: 'integration',
      },
      {
        name: 'HubSpot Integration',
        functionName: 'Calie_hubspot',
        description: 'HubSpot OAuth callback handler',
        testPayload: { action: 'status', tenantId: baseTenantId },
        expectedFields: ['status'],
        requiresAuth: true,
        category: 'integration',
      },
      {
        name: 'Slack Integration',
        functionName: 'Calie_slack',
        description: 'Slack OAuth callback handler',
        testPayload: { action: 'status', tenantId: baseTenantId },
        expectedFields: ['status'],
        requiresAuth: true,
        category: 'integration',
      },
    ];

    return allFunctions;
  }, [realData]);

  const edgeFunctions = getEdgeFunctions();

  // Filter functions by category
  const filteredFunctions = selectedCategory === 'all' 
    ? edgeFunctions 
    : edgeFunctions.filter(f => f.category === selectedCategory);

  // Auto-populate payload when selected function changes
  useEffect(() => {
    const selectedConfig = edgeFunctions.find(f => f.functionName === selectedFunction);
    if (selectedConfig) {
      setCustomPayload(JSON.stringify(selectedConfig.testPayload, null, 2));
    }
  }, [selectedFunction, edgeFunctions]);

  const log = useCallback((level: TestLog['level'], message: string, details?: any, testId?: string) => {
    onTestLog({ level, message, details, testId });
  }, [onTestLog]);

  const createTestResult = useCallback((
    testName: string,
    status: TestResult['status'],
    details: string,
    errorMessage?: string,
    debugData?: any,
    duration?: number
  ): TestResult => {
    const now = new Date();
    return {
      id: Math.random().toString(36).substr(2, 9),
      testName,
      status,
      startTime: now,
      endTime: status !== 'running' ? now : undefined,
      duration: duration || (status !== 'running' ? Math.floor(Math.random() * 2000) + 500 : undefined),
      details,
      errorMessage,
      debugData,
      category: 'Edge Functions',
    };
  }, []);

  const testEdgeFunction = useCallback(async (functionConfig: EdgeFunctionTest, payload?: any) => {
    const testId = `edge-${functionConfig.functionName}-${Date.now()}`;
    const testName = `Edge Function: ${functionConfig.name}`;
    
    setTesting(functionConfig.functionName);
    onTestResult(createTestResult(testName, 'running', 'Invoking edge function...'));
    
    log('info', `Testing edge function: ${functionConfig.functionName}`, { payload: payload || functionConfig.testPayload }, testId);

    try {
      const startTime = performance.now();
      
      const { data, error } = await supabase.functions.invoke(functionConfig.functionName, {
        body: payload || functionConfig.testPayload
      });

      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);

      if (error) {
        throw new Error(`Edge function error: ${error.message}`);
      }

      const validationResults: string[] = [];
      if (functionConfig.expectedFields) {
        for (const field of functionConfig.expectedFields) {
          if (data && typeof data === 'object' && field in data) {
            validationResults.push(`✓ Field '${field}' present`);
          } else {
            validationResults.push(`✗ Field '${field}' missing`);
          }
        }
      }

      const debugData = {
        functionName: functionConfig.functionName,
        category: functionConfig.category,
        payload: payload || functionConfig.testPayload,
        response: data,
        duration,
        validationResults,
        timestamp: new Date().toISOString(),
      };

      const hasValidationErrors = validationResults.some(r => r.startsWith('✗'));
      
      setFunctionStatuses(prev => ({
        ...prev,
        [functionConfig.functionName]: hasValidationErrors ? 'failed' : 'passed'
      }));

      onTestResult(createTestResult(
        testName,
        hasValidationErrors ? 'failed' : 'passed',
        hasValidationErrors 
          ? 'Function completed but response validation failed'
          : `Function completed successfully in ${duration}ms`,
        hasValidationErrors ? 'Response structure validation failed' : undefined,
        debugData,
        duration
      ));

      log('success', `Edge function test completed: ${functionConfig.functionName}`, debugData, testId);

    } catch (error: any) {
      const debugData = {
        functionName: functionConfig.functionName,
        category: functionConfig.category,
        payload: payload || functionConfig.testPayload,
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      };

      setFunctionStatuses(prev => ({
        ...prev,
        [functionConfig.functionName]: 'failed'
      }));

      onTestResult(createTestResult(
        testName,
        'failed',
        `Function failed: ${error.message}`,
        error.message,
        debugData
      ));

      log('error', `Edge function test failed: ${functionConfig.functionName}`, debugData, testId);
    } finally {
      setTesting('');
    }
  }, [onTestResult, log, createTestResult]);

  const testAllFunctions = useCallback(async () => {
    log('info', `🚀 Starting comprehensive edge function tests (${filteredFunctions.length} functions)...`);
    
    for (const functionConfig of filteredFunctions) {
      if (testing) return;
      
      try {
        await testEdgeFunction(functionConfig);
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        log('error', `Failed to test ${functionConfig.functionName}`, error);
      }
    }
    
    log('success', '✅ All edge function tests completed');
  }, [filteredFunctions, testing, testEdgeFunction, log]);

  // Expose testAllFunctions to parent via ref
  useImperativeHandle(ref, () => ({
    testAllFunctions
  }), [testAllFunctions]);

  const testCustomFunction = useCallback(async () => {
    const selectedConfig = edgeFunctions.find(f => f.functionName === selectedFunction);
    if (!selectedConfig) return;

    try {
      const payload = JSON.parse(customPayload);
      await testEdgeFunction(selectedConfig, payload);
    } catch (error: any) {
      log('error', 'Invalid JSON payload', error);
    }
  }, [selectedFunction, customPayload, edgeFunctions, testEdgeFunction, log]);

  const selectedConfig = edgeFunctions.find(f => f.functionName === selectedFunction);

  const categoryLabels: Record<FunctionCategory, string> = {
    all: 'All Functions',
    core: '🔧 Core',
    voice: '📞 Voice/Call',
    sms: '💬 SMS',
    ai: '🤖 AI/ML',
    admin: '⚙️ Admin',
    security: '🔒 Security',
    integration: '🔗 Integration',
  };

  const getCategoryBadgeColor = (category: string) => {
    const colors: Record<string, string> = {
      core: 'bg-blue-500/10 text-blue-700 border-blue-200',
      voice: 'bg-green-500/10 text-green-700 border-green-200',
      sms: 'bg-purple-500/10 text-purple-700 border-purple-200',
      ai: 'bg-orange-500/10 text-orange-700 border-orange-200',
      admin: 'bg-gray-500/10 text-gray-700 border-gray-200',
      security: 'bg-red-500/10 text-red-700 border-red-200',
      integration: 'bg-cyan-500/10 text-cyan-700 border-cyan-200',
    };
    return colors[category] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            <CardTitle>Edge Function Monitor</CardTitle>
          </div>
          <CardDescription>
            Test and monitor all {edgeFunctions.length} Calie.ai edge functions with real-time performance metrics
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Real Data Context Display */}
          {realData && (
            <div className="p-3 bg-muted/50 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Database className="h-4 w-4 text-primary" />
                <span className="font-medium text-sm">Real Data Context</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Tenant:</span>
                  <div className="font-mono">{realData.tenant.name}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Campaigns:</span>
                  <div className="font-mono">{realData.campaigns.length}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Ads:</span>
                  <div className="font-mono">{realData.ads.length}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Numbers:</span>
                  <div className="font-mono">{realData.numbers.length}</div>
                </div>
              </div>
            </div>
          )}

          {loadingData && (
            <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded">
              <Clock className="h-4 w-4 text-primary animate-spin" />
              <span className="text-primary">Loading real user data...</span>
            </div>
          )}

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Filter:</span>
            </div>
            {(Object.keys(categoryLabels) as FunctionCategory[]).map(cat => (
              <Badge 
                key={cat}
                variant={selectedCategory === cat ? "default" : "outline"}
                className={`cursor-pointer ${selectedCategory === cat ? '' : 'hover:bg-muted'}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {categoryLabels[cat]} {cat !== 'all' && `(${edgeFunctions.filter(f => f.category === cat).length})`}
              </Badge>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Function ({filteredFunctions.length} shown)</label>
              <Select value={selectedFunction} onValueChange={setSelectedFunction}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {filteredFunctions.map((func) => (
                    <SelectItem key={func.functionName} value={func.functionName}>
                      <div className="flex items-center gap-2">
                        {functionStatuses[func.functionName] === 'passed' && (
                          <CheckCircle className="h-3 w-3 text-green-600" />
                        )}
                        {functionStatuses[func.functionName] === 'failed' && (
                          <AlertCircle className="h-3 w-3 text-destructive" />
                        )}
                        <span>{func.name}</span>
                        <Badge variant="outline" className={`text-xs ${getCategoryBadgeColor(func.category)}`}>
                          {func.category}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 items-end">
              <Button
                onClick={testCustomFunction}
                disabled={!!testing || !selectedConfig}
                className="flex-1"
              >
                <Play className="h-4 w-4 mr-2" />
                Test Selected
              </Button>
              <Button
                onClick={testAllFunctions}
                disabled={!!testing}
                variant="outline"
              >
                Test All ({filteredFunctions.length})
              </Button>
            </div>
          </div>

          {selectedConfig && (
            <div className="space-y-3">
              <div>
                <h4 className="font-medium mb-1">{selectedConfig.name}</h4>
                <p className="text-sm text-muted-foreground">{selectedConfig.description}</p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Badge variant="outline" className={getCategoryBadgeColor(selectedConfig.category)}>
                    {categoryLabels[selectedConfig.category]}
                  </Badge>
                  <Badge variant={selectedConfig.requiresAuth ? "default" : "secondary"}>
                    {selectedConfig.requiresAuth ? "Requires Auth" : "Public"}
                  </Badge>
                  {selectedConfig.expectedFields && selectedConfig.expectedFields.length > 0 && (
                    <Badge variant="outline">
                      Expects: {selectedConfig.expectedFields.join(', ')}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Test Payload (JSON)</label>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Auto-populated
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCustomPayload(JSON.stringify(selectedConfig.testPayload, null, 2))}
                      className="h-7 text-xs"
                    >
                      Reset to Default
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={customPayload}
                  onChange={(e) => setCustomPayload(e.target.value)}
                  placeholder="Enter custom JSON payload..."
                  className="font-mono text-sm"
                  rows={6}
                />
              </div>
            </div>
          )}

          {testing && (
            <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded">
              <Clock className="h-4 w-4 text-primary animate-spin" />
              <span className="text-primary">Testing {testing}...</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Function Status Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Function Status Overview ({filteredFunctions.length} functions)</CardTitle>
          <CardDescription>Quick overview of all Calie.ai edge functions by category</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredFunctions.map((func) => (
              <div
                key={func.functionName}
                className={`p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors ${
                  selectedFunction === func.functionName ? 'ring-2 ring-primary' : ''
                } ${testing === func.functionName ? 'bg-primary/10' : ''}`}
                onClick={() => setSelectedFunction(func.functionName)}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm truncate">{func.name}</span>
                  {testing === func.functionName ? (
                    <Clock className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                  ) : functionStatuses[func.functionName] === 'passed' ? (
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  ) : functionStatuses[func.functionName] === 'failed' ? (
                    <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                  ) : (
                    <div className="w-4 h-4 rounded-full bg-muted flex-shrink-0" />
                  )}
                </div>
                <div className="text-xs text-muted-foreground mb-2 truncate">
                  {func.functionName}
                </div>
                <div className="flex gap-1 flex-wrap">
                  <Badge variant="outline" className={`text-xs ${getCategoryBadgeColor(func.category)}`}>
                    {func.category}
                  </Badge>
                  {func.requiresAuth && (
                    <Badge variant="outline" className="text-xs">Auth</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

EdgeFunctionMonitor.displayName = 'EdgeFunctionMonitor';
